// Sprite loading. Missing sprites fall back to a soft silhouette so the game
// stays playable while art is still being drawn.
import { SPECIES } from '../shared/species.ts';
import { ELEMENTS } from '../shared/elements.ts';
import { AWAKENING_ICON_STANDIN } from '../shared/awakening.ts';
import { ITEM } from '../shared/items.ts';
import { heldPlaceholder } from './held-icon.ts';

const cache = new Map<string, HTMLImageElement | HTMLCanvasElement>();
let manifest: Record<string, { size: number; aura: boolean }> = {};
/** Creatures drawn with the 3D rig (art/rig3d): animated sheets plus 64px portraits. `dir` is the folder (default creatures/). */
export interface SheetInfo {
  /** Sheet px per cell side; the frame size every draw is laid out in. */
  size: number; walk: number; idle: number; atk: number; portrait: number; dir?: string;
  /** Skins drawn for it: files `<id>~<skin>.png` (shared/skins.ts). */ skins?: string[];
  /** Overrides `<dir>/sheets/<id>.png`. */ src?: string;
  /** Double detail: every creature file also exists under creatures/2x/ with this many image px per sheet px. */ hd?: number;
}
let sheets: Record<string, SheetInfo> = {};
/** Map props and item icons drawn with the 3D rig (art/rig3d/props, art/rig3d/icons): one image, or loop frames side by side. `src` overrides art/<id>.png. */
export interface ArtInfo { w: number; h: number; frames: number; foot: number; src?: string; sizes?: number[] }
let art: Record<string, ArtInfo> = {};
/** Boss portraits (art/rig3d/build-portraits.ts): a close-up over a painted backdrop, portraits/<id>.png. */
let portraits: Record<string, { w: number; h: number }> = {};

export async function loadManifest() {
  const get = async (url: string) => { try { return await (await fetch(url, { cache: 'no-cache' })).json(); } catch { return {}; } };
  let trainer: { sheets?: Record<string, SheetInfo>; stills?: Record<string, ArtInfo> };
  [manifest, sheets, art, trainer, portraits] = await Promise.all([get('sprites/manifest.json'), get('creatures/manifest.json'), get('art/manifest.json'), get('trainer/manifest.json'), get('portraits/manifest.json')]);
  // Trainer outfits (art/rig3d/build-trainer.ts): a sheet per outfit plus standing stills for menus.
  for (const [id, info] of Object.entries(trainer.sheets ?? {})) sheets[id] = { ...info, dir: 'trainer' };
  for (const [id, info] of Object.entries(trainer.stills ?? {})) art[id] = { ...info, src: `trainer/${id}.png` };
  // Creature skins (design/skins-format.md): `<id>~<skin>` files share the creature's layout, so each draws as its own id.
  for (const [id, info] of Object.entries(sheets)) for (const k of info.skins ?? []) sheets[`${id}~${k}`] = { ...info, skins: undefined };
}

export function has(id: string) { return id in art || id in manifest || id in sheets; }
/** Whether the art build ships this skin for a creature (creatures/manifest.json `skins`). */
export function hasSkin(id: string, skin: string) { return !!sheets[id]?.skins?.includes(skin) && `${id}~${skin}` in sheets; }

/**
 * The art an id draws with. An Awakened form (`<species>-awakened`, shared/awakening-data.ts) has its own sprite id;
 * until the art thread draws it, it borrows its base species' art, and every image of it carries a gilt marker:
 * spriteUrl adds `#awakened` (CSS: img[src$="#awakened"]) and creatureIcon an `awk` class.
 */
function artId(id: string): string {
  const from = SPECIES[id]?.awakenedFrom;
  return from && !has(id) ? from : id;
}
/** True for an Awakened form drawn with its base species' art (no art of its own yet). */
export function awakenedArt(id: string): boolean { return artId(id) !== id; }
const awkMark = (id: string, url: string) => (awakenedArt(id) && !url.startsWith('data:') ? `${url}#awakened` : url);
/** A creature picture with the Awakened marker when it is one (Bestiary nodes). */
export function awakenedPic(id: string, aura = false): string {
  return `<img src="${spriteUrl(id, aura)}" alt="">`;
}

/** Size, loop frames and ground line of a rig-drawn prop or icon, else undefined. */
export function artInfo(id: string): ArtInfo | undefined { return art[id]; }

/**
 * A creature walking in place, drawn natively at 40 px, or 48 px when `big` (art/rig3d/build.ts creatures/mini/ and
 * mini-lg/): a trainer's companion in player icons. Undefined for a creature without a rig sheet.
 */
export function companionUrl(id: string, aura = false, big = false): string | undefined {
  const a = artId(id);
  return sheets[a] && !sheets[a].dir ? `creatures/${big ? 'mini-lg' : 'mini'}/${aura ? `${a}_aura` : a}.png` : undefined;
}
/** companionUrl as inline CSS, with the double-detail walk for sharp screens. */
export function companionBg(id: string, aura = false, big = false): string {
  const a = artId(id);
  return creatureBg(a, `${big ? 'mini-lg' : 'mini'}/${aura ? `${a}_aura` : a}.png`);
}

/**
 * Inline CSS for a background image under creatures/ (`<sub>` such as `walk/fenrir.png`), with its double-detail
 * file for sharp screens when the creature has one. The 2x file has the same layout, so background sizes still fit.
 */
export function creatureBg(id: string, sub: string): string {
  const a = `creatures/${sub}`;
  if (!sheets[id]?.hd) return `background-image:url('${a}')`;
  const b = `creatures/2x/${sub}`;
  return `background-image:url('${a}');background-image:-webkit-image-set(url('${a}') 1x,url('${b}') 2x);background-image:image-set(url('${a}') 1x,url('${b}') 2x)`;
}

/**
 * Adds the double-detail file as a 2x candidate to every creature portrait (`<img src="creatures/<id>.png">`) under
 * `root`, now and as the UI renders. Portraits are written as plain src strings all over the UI, so this is the one
 * place that knows about double detail; the 2x candidate keeps the image's intrinsic size.
 */
export function watchCreaturePortraits(root: Element) {
  const scan = (n: Node) => {
    if (!(n instanceof Element)) return;
    if (n instanceof HTMLImageElement) portraitHd(n);
    else for (const img of n.getElementsByTagName('img')) portraitHd(img);
  };
  scan(root);
  new MutationObserver((list) => {
    for (const r of list) {
      if (r.type === 'attributes') {
        // A portrait whose src changed drops the 2x file of its old creature.
        const t = r.target as HTMLImageElement;
        if (t.dataset.hd !== undefined) { t.removeAttribute('srcset'); delete t.dataset.hd; }
        portraitHd(t);
      }
      else r.addedNodes.forEach(scan);
    }
  }).observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['src'] });
}

const PORTRAIT = /^(?:\.?\/)?creatures\/([^/#?]+)\.png(#.*)?$/;
/** Gives a creature portrait its double-detail file as a 2x candidate (see watchCreaturePortraits). */
function portraitHd(img: HTMLImageElement) {
  if (img.hasAttribute('srcset')) return;
  const m = PORTRAIT.exec(img.getAttribute('src') ?? '');
  if (!m || !sheets[m[1].replace(/_aura$/, '')]?.hd) return;
  img.setAttribute('srcset', `creatures/2x/${m[1]}.png${m[2] ?? ''} 2x`);
  img.dataset.hd = '';
}

/**
 * Every rig still shown as an `<img>` (`art/<id>.png` or `art/<id>@<px>.png`) under `root` draws from the file made
 * nearest the screen pixels it covers. build-art.ts renders icons at a ladder of sizes, so a 32 px coin shown at
 * 18 px gets its 18 px drawing (20 at 110% zoom, 36 on a 2x screen) instead of the browser squeezing or stretching
 * the 32, which is what made icons look soft. The picks go in `srcset`: the drawing for the box at 1x, and the one for
 * this screen at the density that fills the same box, so the image keeps its size and nothing on the page moves.
 * Markup only ever names the plain file.
 */
export function watchArtIcons(root: Element) {
  const RE = /^(?:\.?\/)?art\/([^/@#?]+?)(?:@(\d+))?\.png(#.*)?$/;
  /** The box each watched image fills, as last measured: CSS pixels and device pixels across. */
  const across = new WeakMap<HTMLImageElement, { css: number; dev: number }>();
  const watched = new WeakSet<HTMLImageElement>();
  const pick = (img: HTMLImageElement) => {
    // Markup that names its own candidates (the tournament emblems) keeps them.
    if (img.hasAttribute('srcset') && img.dataset.ic === undefined && img.dataset.hd === undefined) return;
    const m = RE.exec(img.getAttribute('src') ?? ''), box = across.get(img);
    const a = m ? art[m[1]] : undefined;
    let set = '';
    if (m && a && box && a.sizes?.length && (a.frames ?? 1) === 1 && !a.src) {
      const all = [a.w, ...a.sizes].sort((x, y) => x - y);
      const url = (n: number) => `art/${m[1]}${n === a.w ? '' : `@${n}`}.png${m[3] ?? ''}`;
      // The size drawn for n pixels: exact, else the next one up (a small squeeze blurs less than a stretch), else the largest.
      const fit = (n: number) => all.find((x) => x >= n - 0.01) ?? all[all.length - 1];
      const one = fit(box.css), here = fit(box.dev), d = +(here / box.css).toFixed(4);
      // Without a 1x candidate the browser would add the markup's own file as one, and pick it on most screens.
      set = here === one || d <= 1 ? `${url(one)} 1x` : `${url(one)} 1x, ${url(here)} ${d}x`;
      if (here === one && url(one) === m[0].replace(/^\.?\//, '')) set = '';
    }
    if (set) {
      if (img.getAttribute('srcset') !== set) img.setAttribute('srcset', set);
      img.dataset.ic = '';
    } else if (img.dataset.ic !== undefined) {
      // Ours no longer fits (the image now shows something else): clear it, and let a creature portrait take its 2x.
      img.removeAttribute('srcset'); delete img.dataset.ic;
      portraitHd(img);
    }
  };
  const ro = new ResizeObserver((entries) => {
    for (const e of entries) {
      const img = e.target as HTMLImageElement;
      const css = e.contentBoxSize?.[0]?.inlineSize ?? e.contentRect.width;
      const dev = Math.max(e.devicePixelContentBoxSize?.[0]?.inlineSize ?? 0, Math.round(css * devicePixelRatio));
      if (css > 0 && dev > 0) { across.set(img, { css, dev }); pick(img); }
    }
  });
  const watch = (img: HTMLImageElement) => {
    if (!RE.test(img.getAttribute('src') ?? '')) return;
    if (!watched.has(img)) {
      watched.add(img);
      try { ro.observe(img, { box: 'device-pixel-content-box' }); } catch { ro.observe(img); }
    }
    pick(img);
  };
  const each = (n: Node, fn: (img: HTMLImageElement) => void) => {
    if (n instanceof HTMLImageElement) fn(n);
    else if (n instanceof Element) for (const img of n.getElementsByTagName('img')) fn(img);
  };
  const drop = (img: HTMLImageElement) => { if (watched.delete(img)) ro.unobserve(img); };
  each(root, watch);
  new MutationObserver((list) => {
    for (const r of list) {
      // A re-render that put the markup's src back, or dropped the srcset, gets the pick again.
      if (r.type === 'attributes') { const t = r.target as HTMLImageElement; if (watched.has(t)) pick(t); else watch(t); }
      else { r.removedNodes.forEach((n) => { if (!n.isConnected) each(n, drop); }); r.addedNodes.forEach((n) => each(n, watch)); }
    }
  }).observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'srcset'] });
}

/** Sheet layout for a creature with an animated sheet, else undefined. */
export function sheetInfo(id: string): SheetInfo | undefined { return sheets[artId(id)]; }
/**
 * The animation sheet image (rows front/left/back/right; walk, idle, attack columns): the double-detail one when it
 * exists. Cut frames from it with sheetCell, and lay them out in sheet px (info.size).
 */
export function sheet(id: string, aura = false): HTMLImageElement | ImageBitmap {
  id = artId(id);
  const key = `sheet:${aura ? `${id}_aura` : id}`;
  let e = sheetCache.get(key);
  if (!e) {
    const s = sheets[id], dir = s?.dir ?? `creatures${s?.hd ? '/2x' : ''}`;
    e = { id, url: s?.src ?? `${dir}/sheets/${aura && !s?.dir ? `${id}_aura` : id}.png`, bm: null, img: null, used: 0, bytes: 0, loading: false };
    sheetCache.set(key, e);
  }
  e.used = performance.now();
  if (e.bm) return e.bm;
  if (e.img) return e.img;
  // First wanted, or freed to stay under the budget and wanted again: (re)load it; it shows a moment later.
  if (!e.loading) loadSheet(e);
  return pendingImg();
}

/*
 * Sheets are big (2880x640 px and up, about 7 MB once decoded). Drawn from an <img> (or turned into an ImageBitmap
 * from one), the browser decodes the whole sheet on the main thread the first time: 25-60 ms each on a laptop-speed
 * CPU. That froze the hunt each time a new species walked in, and the battle board as each fighter first showed.
 * createImageBitmap from the file's bytes (a Blob) decodes off the main thread; until it is done the sheet is not
 * drawable, so the creature appears a frame or two later instead of the game stopping.
 */
interface SheetEntry { id: string; url: string; bm: ImageBitmap | null; img: HTMLImageElement | null; used: number; bytes: number; loading: boolean }
const sheetCache = new Map<string, SheetEntry>();
/** Stands in for a sheet still loading: complete with no pixels, so drawable() is false. */
let pending: HTMLImageElement | null = null;
const pendingImg = () => (pending ??= new Image());
/** Decoded sheets kept at most (bytes); sheets not drawn for a while are freed past it. Phones get less. */
const SHEET_BUDGET = ((typeof navigator !== 'undefined' && (navigator as { deviceMemory?: number }).deviceMemory) || 4) >= 8 ? 512e6 : 256e6;
/** A sheet drawn this recently is never freed (it is on screen). */
const SHEET_KEEP_MS = 15_000;
let sheetBytes = 0;

/**
 * A page loaded before a deploy that resized a creature's frames holds the old layout; cutting the new image with it
 * slides the creature across its frame. The image is the truth: four rows, so its height gives the cell.
 */
function checkSheetSize(at: string, height: number) {
  const info = sheets[at];
  const side = height / 4 / (info?.hd ?? 1);
  if (!info || !Number.isInteger(side) || side === info.size) return;
  for (const [k, v] of Object.entries(sheets)) if (k === at || k.startsWith(`${at}~`)) sheets[k] = { ...v, size: side };
}

function loadSheet(e: SheetEntry) {
  e.loading = true;
  // Without createImageBitmap: a plain <img> (decoded on first draw, as before).
  const plain = () => {
    const img = new Image();
    img.onload = () => checkSheetSize(e.id, img.naturalHeight);
    img.src = e.url;
    e.img = img; e.loading = false;
  };
  if (typeof createImageBitmap !== 'function' || typeof fetch !== 'function') return plain();
  fetch(e.url)
    .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
    .then((b) => createImageBitmap(b))
    .then((bm) => {
      e.loading = false;
      checkSheetSize(e.id, bm.height);
      e.bm = bm; e.bytes = bm.width * bm.height * 4; sheetBytes += e.bytes;
      trimSheets();
    }, plain);
}

function trimSheets() {
  if (sheetBytes <= SHEET_BUDGET) return;
  const now = performance.now();
  const old = [...sheetCache.values()].filter((e) => e.bm && now - e.used > SHEET_KEEP_MS).sort((a, b) => a.used - b.used);
  for (const e of old) {
    if (sheetBytes <= SHEET_BUDGET * 0.8) break;
    e.bm!.close(); e.bm = null; sheetBytes -= e.bytes; e.bytes = 0;
  }
}

/**
 * One sheet cell filled solid in `color` (an opaque CSS colour) through the cell's own alpha, cached. Drawn over the
 * cell at an alpha, it is the hit flash or a tint (white at 0.85 is the flash). Painting the tint on a scratch canvas
 * every frame and drawing that canvas made the browser copy it each time: with no GPU, the costliest thing on the
 * battle board. Cells are small (about 100 KB) and the set in use is small.
 */
const silhouettes = new Map<string, HTMLCanvasElement>();
const imageIds = new WeakMap<object, number>();
let nextImageId = 1;
export function silhouette(img: CanvasImageSource, sx: number, sy: number, n: number, color: string): HTMLCanvasElement {
  let id = imageIds.get(img);
  if (!id) { id = nextImageId++; imageIds.set(img, id); }
  const key = `${id}:${sx},${sy},${n},${color}`;
  let c = silhouettes.get(key);
  if (c) return c;
  // Oldest first out past ~60 MB of cells.
  if (silhouettes.size >= 600) for (const k of [...silhouettes.keys()].slice(0, 100)) silhouettes.delete(k);
  c = document.createElement('canvas'); c.width = c.height = n;
  const x = c.getContext('2d')!;
  x.drawImage(img, sx, sy, n, n, 0, 0, n, n);
  x.globalCompositeOperation = 'source-in';
  x.fillStyle = color; x.fillRect(0, 0, n, n);
  silhouettes.set(key, c);
  return c;
}

/** A CSS colour as [opaque colour, alpha]: '#rrggbbaa', 'rgba(r,g,b,a)', or anything else at alpha 1. */
export function splitAlpha(color: string): [string, number] {
  const hex = /^#([0-9a-f]{6})([0-9a-f]{2})$/i.exec(color);
  if (hex) return [`#${hex[1]}`, parseInt(hex[2], 16) / 255];
  const rgba = /^rgba\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)$/.exec(color);
  if (rgba) return [`rgb(${rgba[1]},${rgba[2]},${rgba[3]})`, Number(rgba[4])];
  return [color, 1];
}

/** Whether an image from sheet() or sprite() can be drawn now: loaded (a sheet still loading is not). */
export function drawable(img: CanvasImageSource): boolean {
  return !(img instanceof HTMLImageElement) || (img.complete && img.naturalWidth > 0);
}
/** Image px per side of one frame in a sheet's image: info.size, or more for double detail. */
export function sheetCell(info: SheetInfo): number { return info.size * (info.hd ?? 1); }
/** The URL of a trainer's animation sheet (CSS backgrounds). */
export function trainerSheetUrl(id: string): string { return sheets[id]?.src ?? `trainer/sheets/${id}.png`; }

/**
 * A trainer sheet and its menu stills in another skin tone and hair colour (`s<skin>c<colour>`, recoloured by
 * server/lookart.ts), as an id of their own: `<base>+<tint>`, stills `<base>+<tint>-<dir>-0`. The base id itself
 * while the trainer manifest isn't loaded.
 */
export function trainerTint(base: string, tint: string): string {
  const info = sheets[base];
  if (!info || info.dir !== 'trainer') return base;
  const id = `${base}+${tint}`;
  if (!sheets[id]) {
    sheets[id] = { ...info, src: `trainer/look/${tint}/sheets/${base}.png` };
    for (const d of ['down', 'left', 'up', 'right']) {
      const still = art[`${base}-${d}-0`];
      if (still) art[`${id}-${d}-0`] = { ...still, src: `trainer/look/${tint}/${base}-${d}-0.png` };
    }
  }
  return id;
}

function placeholder(id: string, aura: boolean): HTMLCanvasElement {
  const sp = SPECIES[id];
  const size = sp ? (sp.rarity === 'legendary' ? 64 : sp.stage === 3 ? 48 : 32) : 16;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  const col = sp ? ELEMENTS[sp.elements[0]].color : '#888';
  g.fillStyle = '#1b2238';
  g.beginPath(); g.ellipse(size / 2, size * 0.62, size * 0.36, size * 0.3, 0, 0, Math.PI * 2); g.fill();
  g.fillStyle = aura ? '#f0c45a' : col;
  g.beginPath(); g.ellipse(size / 2, size * 0.62, size * 0.32, size * 0.26, 0, 0, Math.PI * 2); g.fill();
  g.fillStyle = '#10131f';
  g.fillRect(Math.round(size * 0.36), Math.round(size * 0.52), 2, 2);
  return c;
}

/** Image for a sprite id (creature, tile, prop or icon). */
export function sprite(id: string, aura = false): HTMLImageElement | HTMLCanvasElement {
  id = artId(id);
  const key = aura ? `${id}_aura` : id;
  let img = cache.get(key);
  if (img) return img;
  if (art[id] && !aura) {
    const el = new Image();
    el.src = art[id].src ?? `art/${id}.png`;
    cache.set(key, el);
    return el;
  }
  const m = manifest[id];
  if (!m && sheets[id] && !sheets[id].dir) {
    // Species drawn only with the creature rig (round 2 on) have no 2D sprite: use the rig portrait.
    const el = new Image();
    el.src = `creatures/${key}.png`;
    cache.set(key, el);
    return el;
  }
  if (!m || (aura && !m.aura)) {
    img = SPECIES[id] ? placeholder(id, aura) : placeholder('', false);
    cache.set(key, img);
    return img;
  }
  const el = new Image();
  el.src = `sprites/${key}.png`;
  cache.set(key, el);
  return el;
}

const urls = new Map<string, string>();
export function spriteUrl(id: string, aura = false): string {
  return awkMark(id, baseSpriteUrl(artId(id), aura));
}
function baseSpriteUrl(id: string, aura: boolean): string {
  if (sheets[id] && !sheets[id].dir) return `creatures/${aura ? `${id}_aura` : id}.png`;
  if (art[id] && !aura) return art[id].src ?? `art/${id}.png`;
  const m = manifest[id];
  if (m && (!aura || m.aura)) return `sprites/${aura ? `${id}_aura` : id}.png`;
  const key = aura ? `${id}_aura` : id;
  let u = urls.get(key);
  if (!u) {
    const c = sprite(id, aura);
    u = c instanceof HTMLCanvasElement ? c.toDataURL() : c.src;
    urls.set(key, u);
  }
  return u;
}

/**
 * A creature icon for lists. Creatures with a rig sheet walk in place (CSS steps
 * through a 4-frame strip); the rest fall back to a still image.
 */
export function creatureIcon(id: string, aura = false, alt = ''): string {
  const art = artId(id), awk = art !== id ? ' awk' : '';
  if (!sheets[art]) return `<img src="${spriteUrl(id, aura)}" alt="${alt}">`;
  return `<span class="walker${awk}" role="img" aria-label="${alt}" style="${creatureBg(art, `walk/${aura ? `${art}_aura` : art}.png`)}"></span>`;
}

/**
 * An item's icon. Pass `px`, the CSS size it shows at, to get the drawing made for that size when there is one
 * (build-art.ts draws every Bag item at 48 and 64 px as well as 32), else the 32 px drawing.
 */
export function iconUrl(item: string, px?: number): string {
  // Awakening shards and stones borrow an existing icon, marked, until their own art exists (shared/awakening.ts).
  const standin = AWAKENING_ICON_STANDIN[item];
  if (standin && !has(`icon-${item}`)) return `${spriteUrl(standin)}#awakened`;
  // Ascension stones show their element's icon until their own art exists.
  const el = ITEM[item]?.kind === 'ascension' ? ITEM[item].element : undefined;
  if (el && !has(`icon-${item}`)) return spriteUrl(`icon-el-${el}`);
  // Items with a stand-in (shared/items.ts ItemDef.icon, the ranked charms) show it until their own art exists.
  const stand = ITEM[item]?.icon;
  if (stand && !has(`icon-${item}`)) return spriteUrl(stand);
  // Held items and their pieces (shared/held.ts) show a stand-in gem until their own art exists.
  if (!has(`icon-${item}`)) { const ph = heldPlaceholder(item); if (ph) return ph; }
  const id = `icon-${item}`;
  if (px && art[id]?.sizes?.includes(px) && !art[id].src) return `art/${id}@${px}.png`;
  return spriteUrl(id);
}

/**
 * A UI button icon drawn with the rig (art/rig3d/icons/ui-*.ts), or `fallback` (an SVG) until its art exists. Pass
 * `px`, the CSS size it shows at, to start from the drawing made for that size; without it, the 32 px drawing.
 */
export function uiIcon(id: string, fallback: string, cls = 'ui-ic', px?: number): string {
  const a = art[id];
  if (!a) return fallback;
  const src = (px && px !== a.w && a.sizes?.includes(px) && `art/${id}@${px}.png`) || spriteUrl(id);
  // watchArtIcons swaps in the drawing nearest the screen pixels (2x screens, browser zoom).
  return `<img class="${cls}" src="${src}" alt="" aria-hidden="true">`;
}

/** A boss's painted portrait for the Bosses page and the Ladder, or its sprite until the portrait exists. */
export function bossPic(id: string): string {
  return portraits[id] ? `<img class="bport" src="portraits/${id}.png" alt="">` : `<img src="${spriteUrl(id)}" alt="">`;
}

export function spriteSize(id: string): number {
  id = artId(id);
  if (art[id]) return Math.max(art[id].w, art[id].h);
  return manifest[id]?.size ?? (SPECIES[id] ? (SPECIES[id].rarity === 'legendary' ? 64 : SPECIES[id].stage === 3 ? 48 : 32) : 16);
}
